import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { getType } from "mime";

/**
 * ### iMedic連携ファイル API 操作クラス
 */
export default class IMedicFiles {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  public async gets(userStore: LoginUser, iMedicFilesPrimaryKey: string): Promise<IMedicFilesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      iMedicFilesPrimaryKey: iMedicFilesPrimaryKey
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.IMedicFiles, formatParams);
    const response = await this.commonService.executeGet<IMedicFilesGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);
    return response.data;
  }

  /**
   * ### S3から一時的に公開設定にしたファイルを取得する
   */
  public async getS3File(userStore: LoginUser, expireHour: number, iMedicFile: IMedicFile): Promise<File> {
    const publicS3Url: string = await this.getS3PublicUrl(userStore, expireHour, iMedicFile);
    let mime: string | null = this.getMimeType(iMedicFile.fileName);
    if (mime == null) {
      console.error("IMedicFiles::getS3File() mime is null");
      mime = "";
    }
    const blob: any = await this.getBlobObject(publicS3Url, mime);
    blob.lastModifiedDate = new Date();
    const file = new File([blob], iMedicFile.fileName, { type: mime });
    return file;
  }

  /**
   * ### S3の公開URLを取得する
   */
  private async getS3PublicUrl(userStore: LoginUser, expireHour: number, iMedicFile: IMedicFile): Promise<string> {
    const preUrl = `${RestUrl.S3Url}/${iMedicFile.bucket}/`;
    const imageKey = String(iMedicFile.url).replace(preUrl, "");
    const filePath = decodeURIComponent(imageKey);

    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.PublicS3Url, formatParams);
    const queryParams: any = {
      bucketName: iMedicFile.bucket,
      expireHour: Number(expireHour),
      filePath: filePath
    };
    const response = await this.commonService.executeGet<{ url: string }>(userStore, requestUrl, this.constructor.name, queryParams, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.url;
  }

  /**
   * ### ファイルURLからBLOBオブジェクトを取得する
   */
  private async getBlobObject(publicS3Url: string, mimeType: string): Promise<Blob> {
    const response: any = await window.axios.get(publicS3Url, {
      responseType: "blob",
      headers: { "Content-Type": mimeType }
    });
    return response.data;
  }

  /**
   * ### MimeTypeを取得する
   */
  private getMimeType(fileName: string): string | null {
    /**
     * getTypeの引数にはファイルパスが必要
     * 先頭文字列が「.」の場合、拡張子でありファイルパスではないため、先頭にファイル名を付けてファイルパスにする
     * 先頭文字列が「.」ではない場合、ファイルパスとする
     */
    let contentType: string | null = "";
    if (fileName[0] == ".") {
      contentType = getType(`file${fileName}`);
    } else {
      contentType = getType(`${fileName}`);
    }
    return contentType;
  }
}

export interface IMedicFilesGetModel {
  /** リハビリ計画書 */
  rehabilitationPlanFiles: Array<FileByUseOfMonth>;
  /** 口腔機能向上計画書 */
  oralFunctionImprovementPlanFiles: Array<FileByUseOfMonth>;
  /** 褥瘡マネジメント計画書 */
  bedsoreManagementPlanFiles: Array<FileByUseOfMonth>;
  /** 排泄支援計画書 */
  excretionSupportPlanFiles: Array<FileByUseOfMonth>;
  /** 自立支援評価・計画書 */
  independenceSupportEvaluationPlanFiles: Array<FileByUseOfMonth>;
  /** 栄養ケア計画書 */
  nutritionCarePlanFiles: Array<FileByUseOfMonth>;
  /** 窓口請求書_請求書 */
  counterInvoiceBillFiles: Array<FileByUseOfMonth>;
  /** 窓口請求書_領収書 */
  counterInvoiceReceiptFiles: Array<FileByUseOfMonth>;
  /** 総合計画書 */
  comprehensivePlanFiles: Array<FileByUseOfMonth>;
}

export interface FileByUseOfMonth {
  /** 利用月 */
  useOfMonth: number;
  /** ファイル */
  filesByCreated: Array<FileByCreated>;
}

export interface FileByCreated {
  /** 利用月 */
  useOfMonth: number;
  /** 作成日時 */
  created: number;
  /** ファイル */
  files: Array<IMedicFile>;
}

export interface IMedicFile {
  /** パーティションキー */
  iMedicFilesPrimaryKey: string;
  /** 事業所番号 */
  careOfficeNumber: string;
  /** サービス種別 */
  careOfficeServiceType: string;
  /** ソートキー */
  iMedicFilesSortKey: string;
  /** 文書種別 */
  documentType: DocumentType;
  /** 作成日時 */
  created: number;
  /** 保険者番号 */
  insuredNumber: string;
  /** 介護保険被保険者番号 */
  careInsuranceInsuredNumber: string;
  /** 利用月（yyyymm 形式） */
  monthOfUse: number;
  /** S3 バケット名 */
  bucket: string;
  /** S3 のファイル URL */
  url: string;
  /** ファイル名 */
  fileName: string;
  /** 論理削除フラグ */
  deleteFlag: boolean;
}

export enum DocumentType {
  /** リハビリ計画書 */
  REHABILITATION_PLAN = "リハビリ計画書",
  /** 口腔機能向上計画書 */
  ORAL_FUNCTION_IMPROVEMENT_PLAN = "口腔機能向上計画書",
  /** 褥瘡マネジメント計画書 */
  BEDSORE_MANAGEMENT_PLAN = "褥瘡マネジメント計画書",
  /** 排泄支援計画書 */
  EXCRETION_SUPPORT_PLAN = "排泄支援計画書",
  /** 自立支援評価・計画書 */
  INDEPENDENCE_SUPPORT_EVALUATION_PLAN = "自立支援評価・計画書",
  /** 栄養ケア計画書 */
  NUTRITION_CARE_PLAN = "栄養ケア計画書",
  /** 窓口請求書_請求書 */
  COUNTER_INVOICE_BILL = "窓口請求書_請求書",
  /** 窓口請求書_領収書 */
  COUNTER_INVOICE_RECEIPT = "窓口請求書_領収書",
  /** 総合計画書 */
  COMPREHENSIVE_PLAN = "総合計画書"
}
